import {
    Box,
    Typography,
    Stack,
    Grid,
    useTheme,
    alpha,
    Divider,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';

interface HotkeyEntry {
    id: string;
    function: string;
    tool: string;
    input: string;
    action: string;
    subActionOf?: string;
}

const hotkeys: HotkeyEntry[] = [
    // ── Hiding patient information ──
    {
        id: 'toggle-sensitive-info',
        function: 'Toggle Sensitive Info',
        tool: 'Any tool selected',
        input: 'Ctrl + H',
        action: 'Show/hide sensitive patient information (e.g. name, ID)',
    },
    // ── Navigation ──
    {
        id: 'navigate-scroll',
        function: 'Navigation',
        tool: 'Any tool selected',
        input: 'Mouse wheel OR Arrow keys (↑/↓) OR Page Up/Page Down',
        action: 'Scroll through slices in all 2D views',
    },
    {
        id: 'select-plane',
        function: 'Select Main Viewport Plane',
        tool: 'Any tool selected',
        input: 'A / C / S',
        action: 'Show the axial (A), coronal (C), or sagittal (S) plane in the main viewport. Inactive while typing in a text field or while the side-by-side view is open.',
    },
    {
        id: 'navigate-pan-global',
        function: 'Pan Image',
        tool: 'Any tool selected',
        input: 'Middle mouse button + drag',
        action: 'Move image within display area',
    },
    {
        id: 'navigate-pan-space',
        function: '',
        tool: '',
        input: 'Space + Left mouse button + drag (Cmd on Mac)',
        action: 'Move image within display area (alternative)',
    },
    // ── Pan Tool ──
    {
        id: 'navigate-pan-tool',
        function: 'Pan Tool',
        tool: 'Dedicated pan tool selected',
        input: 'Left mouse button + drag',
        action: 'Move image within display area (dedicated tool)',
    },
    {
        id: 'navigate-pan-tool-select',
        function: '',
        tool: '',
        input: 'Left mouse button click (no drag)',
        action: 'Select a structure (if hovering over one)',
    },
    // ── Zoom ──
    {
        id: 'zoom',
        function: 'Zoom',
        tool: 'Any tool selected',
        input: 'Ctrl + Mouse wheel',
        action: 'Zoom in/out on image',
    },
    // ── Window/Level ──
    {
        id: 'wl-main',
        function: 'Window/Level Adjustment',
        tool: 'Any tool selected',
        input: 'Right mouse button + drag',
        action: 'Adjust image contrast (global)',
    },
    {
        id: 'wl-vertical',
        function: '',
        tool: '',
        input: 'Vertical mouse movement',
        action: 'Adjust window center (brightness)',
    },
    {
        id: 'wl-horizontal',
        function: '',
        tool: '',
        input: 'Horizontal mouse movement',
        action: 'Adjust window width (contrast)',
    },
    {
        id: 'wl-secondary',
        function: '',
        tool: '',
        input: 'Alt + Right mouse button + drag',
        action: 'Adjust secondary image window/level (when loaded)',
        subActionOf: 'wl-main',
    },
    {
        id: 'wl-esc',
        function: '',
        tool: '',
        input: 'Esc (during adjustment)',
        action: 'Cancel and restore previous window/level',
        subActionOf: 'wl-main',
    },
    // ── Brush Tool ──
    {
        id: 'brush-radius',
        function: 'Brush Tool',
        tool: '2D Brush tool selected',
        input: 'Shift + Mouse wheel',
        action: 'Change brush radius',
    },
    {
        id: 'brush-erase',
        function: '',
        tool: '',
        input: 'Alt + Left mouse button',
        action: 'Enable erase mode',
    },
    {
        id: 'brush-cancel',
        function: '',
        tool: '',
        input: 'Esc (during drawing)',
        action: 'Cancel current brush stroke',
    },
    // ── Line Draw Tool ──
    {
        id: 'line-draw',
        function: 'Line Draw Tool',
        tool: 'Line Draw tool selected',
        input: 'Left mouse button click / drag',
        action: 'Draw contour point-by-point or freeform',
    },
    {
        id: 'line-close',
        function: '',
        tool: '',
        input: 'Left mouse button double-click',
        action: 'Close the current line drawing (connect to start point)',
    },
    {
        id: 'line-undo',
        function: '',
        tool: '',
        input: 'Right mouse button click',
        action: 'Undo last drawn segment',
    },
    {
        id: 'line-shift',
        function: '',
        tool: '',
        input: 'Hold Shift',
        action: 'Disable snap-to-close (draw through start point)',
    },
    {
        id: 'line-cancel',
        function: '',
        tool: '',
        input: 'Esc (during drawing)',
        action: 'Discard current line drawing',
    },
    // ── Eraser Tool ──
    {
        id: 'eraser',
        function: 'Eraser Tool',
        tool: 'Eraser tool selected',
        input: 'Left mouse button click / drag',
        action: 'Erase contours point-by-point or freeform',
    },
    {
        id: 'eraser-cancel',
        function: '',
        tool: '',
        input: 'Esc (during erasing)',
        action: 'Cancel current erasing action',
    },
    {
        id: 'eraser-shift',
        function: '',
        tool: '',
        input: 'Shift + Mouse wheel',
        action: 'Adjust eraser size (when erasing)',
    },
    // ── Transform Tool ──
    {
        id: 'transform',
        function: 'Transform Tool',
        tool: 'Transform tool selected',
        input: 'Left mouse button click + drag',
        action: 'Translate, resize/scale, or rotate the structure depending on where you click and drag',
    },
    // ── Line Deform Tool ──
    {
        id: 'line-deform',
        function: 'Line Deform Tool',
        tool: 'Line Deform tool selected',
        input: 'Left mouse button click + drag',
        action: 'Click and drag to deform line',
    },
    {
        id: 'line-deform-cancel',
        function: '',
        tool: '',
        input: 'Esc (during deformation)',
        action: 'Cancel current deformation and revert to previous line shape',
    },
    {
        id: 'line-deform-shift',
        function: '',
        tool: '',
        input: 'Shift + Mouse wheel',
        action: 'Adjust percentage of contour length to deform (when deforming)',
    },
    // ── Registration Tool ──
    {
        id: 'registration-swap',
        function: 'Registration Tool',
        tool: 'Registration tool selected',
        input: 'Hold Shift',
        action: 'Temporarily swap rotation/translation mode',
    },
    {
        id: 'registration-cancel',
        function: '',
        tool: '',
        input: 'Esc (during action)',
        action: 'Cancel and revert to previous translation/rotation',
    },
    {
        id: 'registration-translate-keys',
        function: '',
        tool: '',
        input: 'Ctrl + Arrow keys (Cmd on Mac)',
        action: 'Translate secondary image 0.5 mm per press (Translation mode)',
        subActionOf: 'registration-swap',
    },
    {
        id: 'registration-rotate-keys',
        function: '',
        tool: '',
        input: 'Ctrl + ←/→ (Cmd on Mac)',
        action: 'Rotate secondary image ~0.5° per press (Rotation mode)',
        subActionOf: 'registration-swap',
    },
    // ── Structure Management ──
    {
        id: 'structure-management',
        function: 'Structure Management',
        tool: 'Structure selected',
        input: 'Delete (Fn + Backspace on Mac)',
        action: 'Brush, line tool, eraser and line deform: remove contours on current slice; Other tools: open confirmation dialog to delete the entire structure',
    },
    {
        id: 'structure-management-shift',
        function: '',
        tool: '',
        input: 'Shift + Delete',
        action: 'Other tools only: delete the entire structure immediately (skip confirmation dialog)',
        subActionOf: 'structure-management',
    },
];

function renderInput(input: string): JSX.Element {
    return (
        <Box
            component="span"
            sx={(theme) => ({
                display: 'inline-block',
                fontFamily: 'monospace',
                fontSize: 12,
                backgroundColor: alpha(theme.palette.primary.light, 0.15),
                color: theme.palette.primary.light,
                border: `1px solid ${alpha(theme.palette.primary.light, 0.25)}`,
                borderRadius: '6px',
                px: 0.75,
                whiteSpace: 'pre-wrap',
            })}
        >
            {input}
        </Box>
    );
}

export default function HotkeysHelp() {
    const theme = useTheme();

    return (
        <Box sx={{ maxHeight: 500, overflowY: 'auto', pr: 1 }}>
            <Stack
                spacing={2}
                sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                    borderRadius: 2,
                    p: 2,
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography
                            variant="subtitle1"
                            fontWeight={600}
                            color="primary.light"
                        >
                            <FormattedMessage id="shortcutsAndControls" />
                        </Typography>
                    </Box>

                    <Divider
                        sx={{
                            borderColor: alpha(theme.palette.primary.main, 0.2),
                        }}
                    />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {hotkeys.map((entry) => (
                        <Grid container spacing={2} key={entry.id}>
                            <Grid item xs={6}>
                                <Typography fontWeight={500} fontSize={14}>
                                    {entry.function}
                                </Typography>
                                {entry.tool && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontStyle: 'italic' }}
                                    >
                                        {entry.tool}
                                    </Typography>
                                )}
                            </Grid>

                            <Grid item xs={6}>
                                {renderInput(entry.input)}
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block' }}
                                >
                                    {entry.action}
                                </Typography>
                            </Grid>
                        </Grid>
                    ))}
                </Box>
            </Stack>
        </Box>
    );
}
